<template>
  <div class="sources-block" :style="{ background: bg }">
    <div v-if="rows.length" class="rows">
      <div v-for="row in rows" :key="row.key" class="row">
        <!-- Блюдо -->
        <div class="line line--dish" :class="{ 'cursor-pointer': row.parts.length }" @click="toggleRow(row)">
          <span class="name">{{ row.name }}</span>
          <span class="value"><span v-if="row.approx" class="approx-mark">~</span>{{ fmt(row.val) }}</span>
        </div>

        <!-- Из чего набрался нутриент внутри блюда — по тапу на блюдо -->
        <template v-if="opened.has(row.key)">
          <div v-for="part in row.parts" :key="part.key" class="line line--part">
            <span class="name">{{ part.name }}</span>
            <span class="value"><span v-if="part.approx" class="approx-mark">~</span>{{ fmt(part.val) }}</span>
          </div>
        </template>
      </div>
    </div>

    <p v-else class="empty">{{ emptyText }}</p>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue'

// Раскладка блюда по умолчанию свёрнута и открывается тапом по блюду:
// разворачивать всё сразу было решено ещё для клетчатки с её короткими
// списками, но при шести раскрытиях день превращался в простыню строк.
const opened = reactive(new Set())
function toggleRow(row) {
  if (!row.parts.length) return
  if (opened.has(row.key)) opened.delete(row.key)
  else opened.add(row.key)
}


// Раскрытие полоски макронутриента за день: из каких блюд он набрался.
// Обобщение бывшего FiberBlock — той же математикой ходят клетчатка
// (macro="fiber"), белок (macro="protein") и вода (macro="water"). Знак «~»
// есть у клетчатки (оценка ИИ) и у воды (оценка по массовому балансу):
// остальные макросы без оценок.
const props = defineProps({
  // Записи дня в том виде, в каком их отдаёт GET /api/diary
  entries: { type: Array, default: () => [] },
  // Ключ per100 в раскладке и, если field не задан, имя поля записи
  macro: { type: String, default: 'fiber' },
  // Имя поля записи, когда оно отличается от ключа per100: вода лежит в
  // записи как water_ml, а в раскладке — как water
  field: { type: String, default: null },
  // Ниже этого числа строка показывается как «0»: список «откуда» отвечал бы
  // «ниоткуда» и только удлинялся. У клетчатки порог 0,05 (чеснок и базилик в
  // пасте дают по 0,04 г); у белка — 0,5: белок есть почти везде, и без
  // порога чай со специями шумели бы в списке источников.
  minShown: { type: Number, default: 0.05 },
  // Фон продолжает полоску: цвет MacroBar с той же альфой 0.2
  bg: { type: String, default: 'rgba(166, 183, 121, 0.2)' },
  emptyText: { type: String, default: 'Сегодня клетчатки в дневнике пока нет' },
  // Ккал показываются целыми: доли калории не имеют смысла.
  // Граммы клетчатки и белка ниже 10 сохраняют десятые.
  integer: { type: Boolean, default: false },
})

// Граммы — всегда с одним знаком, без переключения на целые при ≥10.
// Раньше тунец 15.6 показывался как «16», блюдо 15.6+9.8=25.4 — как «25»,
// и видимые строки «16 + 9.8» не сходились с «25»: независимое округление
// строк враньём не является, но выглядит именно так. Точности 0.1 хватает,
// чтобы вертикальная арифметика сходилась (жалоба пользователя #4).
function fmt(v) {
  if (props.integer) return Math.round(v)
  return Math.round(v * 10) / 10
}

function valueOf(entry) {
  return parseFloat(entry[props.field || props.macro]) || 0
}

// Значение ингредиента внутри рецепта: в JSONB оно хранится на 100 г,
// а вес — свой у каждого ингредиента.
function ingredientValue(ing) {
  const per100 = parseFloat(ing?.per100?.[props.macro])
  const weight = parseFloat(ing?.weight_g)
  if (!Number.isFinite(per100) || !Number.isFinite(weight)) return 0
  return per100 * weight / 100
}

// «~» — у клетчатки с ИИ-оценкой и у воды, посчитанной по балансу
function isApprox(entry) {
  if (props.macro === 'fiber') return entry.fiber_source === 'ai'
  if (props.macro === 'water') return entry.water_source === 'balance' || entry.water_source === 'ai'
  return false
}

const rows = computed(() => {
  // Группировка та же, что на карточках дневника: записи с общим dish_id —
  // одно блюдо, одиночная запись — сама себе блюдо.
  const groups = new Map()
  for (const e of props.entries) {
    const key = e.dish_id ? `dish-${e.dish_id}` : `entry-${e.id}`
    if (!groups.has(key)) groups.set(key, { key, name: null, entries: [], dishIngredients: null })
    const g = groups.get(key)
    g.entries.push(e)
    if (e.dish_name && !g.name) g.name = e.dish_name
    if (e.dish_ingredients && !g.dishIngredients) g.dishIngredients = e.dish_ingredients
  }

  const out = []
  for (const g of groups.values()) {
    const val = g.entries.reduce((sum, e) => sum + valueOf(e), 0)
    // Блюда без нутриента не показываем: нули забили бы список,
    // не отвечая на вопрос «из чего набралось».
    if (val < props.minShown) continue

    out.push({
      key: g.key,
      name: g.name || g.entries[0]?.product_name || 'Без названия',
      val,
      parts: buildParts(g),
      approx: g.entries.some(e => isApprox(e) && valueOf(e) > 0),
    })
  }

  return out.sort((a, b) => b.val - a.val)
})

// Раскладка блюда собирается по самому глубокому уровню, который есть.
//
// Структура двухуровневая: zoya_entries хранит по строке на позицию с тарелки,
// а из чего собрана сама позиция — в zoya_dishes.ingredients[].ingredients[].
// Поэтому «Обед с рагу» на верхнем уровне даёт яйцо, хлеб и рагу, но интересно
// не это, а фасоль с морковью внутри рагу. Позиции без раскладки (хлеб) идут
// как есть — они и есть свой собственный ингредиент.
//
// Считаем ОТ ЗАПИСЕЙ, а не от JSONB. Итог блюда — сумма по zoya_entries, и
// раньше раскладка бралась из другого источника: JSONB хранит снимок на момент
// распознавания, где у части ингредиентов значений просто нет. Обед с пастой
// показывал 8,8 в шапке и 4,7 в списке — огурец в снимке был без волокон и
// пропадал вовсе. Теперь каждая строка привязана к записи, а снимок задаёт
// только пропорции внутри позиции.
function buildParts(group) {
  const items = Array.isArray(group.dishIngredients) ? group.dishIngredients : null

  // Блюдо записано одной строкой, а его состав — в блюде: разворачиваем целиком.
  if (group.entries.length === 1 && items && items.length) {
    const e = group.entries[0]
    return finishParts(spread(flatten(items), valueOf(e), e), group)
  }

  const parts = []
  group.entries.forEach((e, idx) => {
    const val = valueOf(e)
    if (val < props.minShown) return
    const item = matchItem(items, e, idx, group.entries.length)
    const nested = item && Array.isArray(item.ingredients) ? item.ingredients : null
    const inner = nested && nested.length ? spread(flatten(nested), val, e) : []
    if (inner.length) parts.push(...inner)
    else parts.push({ key: `e${e.id}`, name: e.product_name, val, approx: isApprox(e) })
  })

  return finishParts(parts, group)
}

// Позиция блюда, соответствующая записи. Порядок записей и позиций совпадает,
// пока блюдо не редактировали, поэтому индекс — основной способ; по названию —
// запасной, на случай удалённой или добавленной позиции.
function matchItem(items, entry, idx, count) {
  if (!items) return null
  if (items.length === count && items[idx]) return items[idx]
  return items.find(it => norm(it.name) === norm(entry.product_name)) || null
}

// Самый глубокий уровень раскладки: [{ name, val }].
function flatten(items) {
  const out = []
  for (const item of items) {
    const nested = Array.isArray(item.ingredients) ? item.ingredients : null
    if (nested && nested.length) out.push(...flatten(nested))
    else out.push({ name: item.name, val: ingredientValue(item) })
  }
  return out
}

// Разложить фактическое значение записи по пропорциям из снимка.
// Снимок задаёт только соотношение: сумма строк всегда равна значению записи,
// иначе раскладка снова разойдётся с шапкой. Если в снимке нутриента нет
// вовсе — пропорций не из чего строить, и позиция остаётся одной строкой.
function spread(raw, target, entry) {
  if (!(target > 0)) return []
  const total = raw.reduce((sum, p) => sum + p.val, 0)
  if (!(total > 0)) return []
  const k = target / total
  return raw
    .filter(p => p.val > 0)
    .map((p, i) => ({
      key: `e${entry.id}-${i}`,
      name: p.name,
      val: p.val * k,
      approx: isApprox(entry),
    }))
}

function finishParts(parts, group) {
  const shown = parts.filter(p => p.val >= props.minShown).sort((a, b) => b.val - a.val)

  // Прячем раскладку только когда она буквально повторила бы строку блюда:
  // одна составляющая с тем же названием. Раньше здесь стояло «больше одной»,
  // и из-за этого «Хлеб и яйца» терял строку «Хлеб ржаной» — яйца дают ноль
  // и отфильтровываются, а оставшийся хлеб как раз и отвечает, откуда
  // набрался нутриент в этом блюде.
  if (shown.length === 1) {
    const same = norm(shown[0].name) === norm(group.name || group.entries[0]?.product_name)
    return same ? [] : shown
  }
  return shown
}

function norm(v) {
  return String(v || '').trim().toLowerCase()
}
</script>

<style scoped>
/* Фон и нижнее скругление продолжают полоску MacroBar: тот же цвет заливки
   незаполненной части (color + 20% альфы), поэтому вместе они читаются одной
   областью, а не двумя блоками друг под другом.

   Верхняя линия — ::before в потоке, а не border-top: горизонтальный паддинг
   блока укорачивает её до той же ширины, что у разделителей строк ниже —
   линия во всю ширину выбивалась из их ряда. Тон тот же. */
.sources-block {
  padding: 0 10px 8px;
  border-radius: 0 0 12px 12px;
  margin-bottom: 6px;
}
.sources-block::before {
  content: '';
  display: block;
  height: 1px;
  margin-bottom: 2px;
  background: rgba(65, 41, 26, 0.09);
}

.line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid rgba(65, 41, 26, 0.09);
}
.row:last-child .line:last-child { border-bottom: none; }

.line--dish .name {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
  line-height: 1.25;
  flex: 1 1 auto;
  min-width: 0;
}
.line--dish .value {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
}

/* Ингредиенты отличаются от блюда только отступом и весом шрифта: кегль тот же,
   цвет фирменный без прозрачности — приглушённый серый на цветном фоне читался
   слишком бледно. */
.line--part { padding-left: 14px; }
.line--part .name {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 400;
  color: var(--ink);
  line-height: 1.25;
  flex: 1 1 auto;
  min-width: 0;
}
.line--part .value {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 400;
  color: var(--ink);
  white-space: nowrap;
}

/* Знак «~» — общий класс .approx-mark в style.css: он одинаков здесь,
   в полоске и в разбивке за период, и разъезжаться им нельзя. */

.empty {
  font-family: Inter, sans-serif;
  font-size: 12.5px;
  color: var(--ink-70);
  margin: 0;
  padding: 6px 0;
}

</style>
